import React from 'react'
import { useLocation, useParams } from 'react-router-dom'
import * as Api from 'src/api'
import PortfolioIcon from 'src/assets/icons/customIcons/menu-icons/Portfolio'
import { useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useLocale } from 'src/hooks/locale/locale'
import { useTranslatable } from 'src/hooks/locale/utils'
import BackButton from 'src/views/components/BackButton'
import SubPageLayout from 'src/views/includes/SubPageLayout'
import SuspenseWrapper from 'src/views/includes/SuspenseWrapper'
import { SectionProvider } from './SectionContext'

const PAGES = [
  { title: 'portfolio:general', link: 'general' },
  { title: 'portfolio:competencies', link: 'competencies' },
  { title: 'portfolio:semesters_and_mentors', link: 'semesters' },
] as const

export default function AdministratorPortfolioSectionLayout(): JSX.Element {
  return (
    <SuspenseWrapper>
      <PageContent />
    </SuspenseWrapper>
  )
}

function PageContent(): JSX.Element {
  const t = useTranslatable()
  const locale = useLocale()
  const { pathname } = useLocation()
  const { id } = useParams()
  const headers = useAuthenticatedHeaders()
  const {
    data: section,
    isLoading,
    mutate,
  } = useApi({
    endpoint: Api.getAdministratorPortfolioSection,
    params: React.useMemo(() => ({ headers, args: { id: id! } }), [headers, id]),
    shouldFetch: id != null,
  })

  const translatedName = React.useMemo(() => {
    if (pathname.includes('general')) return t('portfolio:general')
    if (pathname.includes('competencies')) return t('portfolio:competencies')
    return t('portfolio:semesters_and_mentors')
  }, [pathname, t])

  const subBreadcrumbsItems = React.useMemo(() => {
    const basePath = `/${locale}/administrator/portfolio/sections`
    return [
      { page: `${t('portfolio:portfolio')} - ${t('portfolio:sections')}`, path: basePath },
      { page: id != null ? (section?.name ?? '') : t('common:add'), path: pathname },
      { page: translatedName, path: pathname },
    ]
  }, [locale, t, id, section?.name, translatedName, pathname])

  const pages = React.useMemo(() => PAGES.map(({ title, link }) => ({ title: t(title), link })), [t])

  return (
    <SectionProvider section={section} isLoading={isLoading} mutate={mutate}>
      <SubPageLayout
        Icon={PortfolioIcon}
        subBreadcrubms={subBreadcrumbsItems}
        title={t('portfolio:portfolio')}
        rightElement={<BackButton navigateTo={`/${locale}/administrator/portfolio/sections`} />}
        pages={pages}
      />
    </SectionProvider>
  )
}
